import { useEffect, useRef, useState } from 'react';

/**
 * Counts an integer up to its final value.
 *
 * Only used for the end-of-session scores, where the number *is* the result and
 * arriving at it is worth a beat. Eased out, never linear, and — per §6.5 —
 * skipped entirely under `prefers-reduced-motion`, which gets the final figure
 * immediately rather than a slower animation.
 */
export function useCountUp(
  target: number,
  options: { duration?: number; delay?: number; from?: number } = {},
): number {
  /*
   * `from` defaults to zero, which is right for a final total arriving on the
   * session screen. A *running* total is a different thing: counting a pool
   * score up from zero after every hand would animate a number that never
   * happened. The hand summary passes the score before this hand instead, so
   * what animates is the change.
   */
  const { duration = 900, delay = 0, from = 0 } = options;
  const [value, setValue] = useState(target);
  const frame = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      duration <= 0
    ) {
      setValue(target);
      return;
    }

    setValue(from);
    const run = () => {
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        // Cubic ease-out: fast enough to read as a count, settled by the end.
        const eased = 1 - (1 - t) ** 3;
        setValue(Math.round(from + (target - from) * eased));
        if (t < 1) frame.current = requestAnimationFrame(step);
      };
      frame.current = requestAnimationFrame(step);
    };

    if (delay > 0) timer.current = setTimeout(run, delay);
    else run();

    return () => {
      cancelAnimationFrame(frame.current);
      clearTimeout(timer.current);
    };
  }, [target, duration, delay, from]);

  return value;
}
